/*
 * ILS - Custom breadcrumb behaviour (pendulum + manual drag-scrub).
 * Default: overflowing breadcrumb auto-glides (rest left 2s -> glide left until
 * the tail is flush right -> rest 2s -> jump back, repeat) via a single GPU
 * transform on a wrapper (smooth, no per-frame scrollLeft).
 * TASK-1936: the user can grab the row with a finger and drag it left/right by
 * hand. Pointer capture is taken on pointerdown so EVERY pointermove reaches us
 * (otherwise the browser hands the gesture to vertical page scroll and the row
 * only nudges a few px). CSS touch-action:pan-y keeps vertical page scroll
 * working while we own the horizontal axis. While dragging the auto-glide is
 * paused and the track follows the finger 1:1, clamped to 0..-overflow. On
 * release the row holds still for 3s, then the pendulum resumes FROM the current
 * position. A real tap on a crumb link still navigates (drag only engages past a
 * small movement threshold; a no-move touch is treated as a tap).
 * Fits on one line -> static. Respects reduced-motion. Mounted globally.
 *
 * TASK-1941: only restart the pendulum on a real WIDTH change. On mobile the
 * sticky header collapsing on slow scroll (and the URL bar showing/hiding)
 * fires window 'resize' with the SAME width but a different height. The old
 * code called start() on every resize, which re-measured and kicked off the
 * auto-glide mid-collapse, so the crumb row appeared to "suck" itself sideways
 * / upward exactly while the header folded. We now remember the last viewport
 * width and ignore resize events that don't change it.
 */
(function () {
  'use strict';

  var REST = 2000;            // ms rest at each end of the auto-glide
  var SPEED = 25;             // px per second during the glide
  var MIN_SCROLL = 1800;      // ms minimum glide duration
  var HOLD_AFTER_DRAG = 3000; // ms the row stays still after the finger lifts
  var DRAG_THRESHOLD = 5;     // px of movement before a touch counts as a drag

  function px(v) { return parseFloat(v) || 0; }
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function setup(bc) {
    if (!bc || bc.dataset.ilsBcInit === '1') return;
    bc.dataset.ilsBcInit = '1';

    var host = bc.closest('.ils-bc-host') || bc.parentElement;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var track = null, anim = null;
    var overflow = 0;        // max travel (px, positive)
    var pos = 0;             // current static position (px, 0..-overflow)
    var holdTimer = null;

    // pointer/drag state
    var pointerDown = false;
    var dragging = false;
    var startX = 0;
    var startPos = 0;
    var activeId = null;
    var captured = false;

    function ensureTrack() {
      if (track && track.parentNode === bc) return;
      track = document.createElement('span');
      track.className = 'ils-bc__track';
      while (bc.firstChild) track.appendChild(bc.firstChild);
      bc.appendChild(track);
    }

    function measure() {
      var cs = getComputedStyle(bc);
      var availW = bc.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight);
      overflow = Math.round(track.scrollWidth - availW);
      if (overflow < 0) overflow = 0;
    }

    function cancelAnim() {
      if (anim) { try { anim.cancel(); } catch (e) {} anim = null; }
    }
    function clearHold() {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
    }

    function setX(x) {
      pos = x;
      track.style.transform = 'translateX(' + x + 'px)';
    }

    // Read where the track visually is right now (works mid-animation).
    function currentTranslate() {
      try {
        var t = getComputedStyle(track).transform;
        if (t && t !== 'none') {
          var m = t.match(/matrix.*\((.+)\)/);
          if (m) {
            var parts = m[1].split(',').map(function (s) { return parseFloat(s); });
            var tx = parts.length === 6 ? parts[4] : parts[12];
            if (!isNaN(tx)) return tx;
          }
        }
      } catch (e) {}
      return pos;
    }

    // Auto pendulum from a given start offset (<=0).
    function startPendulum(fromPos) {
      cancelAnim();
      if (reduce || overflow <= 2) {
        bc.classList.remove('ils-bc--pend');
        if (host) host.classList.remove('ils-bc--anim');
        setX(0);
        return;
      }
      bc.classList.add('ils-bc--pend');
      if (host) host.classList.add('ils-bc--anim');

      var from = clamp(fromPos != null ? fromPos : 0, -overflow, 0);
      var scrollMs = Math.max(Math.round(overflow / SPEED * 1000), MIN_SCROLL);
      var remaining = overflow + from; // px still to travel to reach -overflow

      if (remaining > 4 && from > -overflow) {
        // finish current pass from where we are, then start the endless loop
        var partMs = Math.max(Math.round(remaining / SPEED * 1000), 400);
        var kfPart = [
          { transform: 'translateX(' + from + 'px)', offset: 0 },
          { transform: 'translateX(-' + overflow + 'px)', offset: clamp(partMs / (partMs + REST), 0.01, 0.99) },
          { transform: 'translateX(-' + overflow + 'px)', offset: 1 }
        ];
        try {
          anim = track.animate(kfPart, { duration: partMs + REST, easing: 'linear' });
          anim.onfinish = function () { loop(); };
          return;
        } catch (e) { /* fall through */ }
      }
      loop();

      function loop() {
        cancelAnim();
        setX(0);
        var total = REST + scrollMs + REST;
        var kf = [
          { transform: 'translateX(0px)', offset: 0 },
          { transform: 'translateX(0px)', offset: REST / total },
          { transform: 'translateX(-' + overflow + 'px)', offset: (REST + scrollMs) / total },
          { transform: 'translateX(-' + overflow + 'px)', offset: 1 }
        ];
        try { anim = track.animate(kf, { duration: total, iterations: Infinity, easing: 'linear' }); } catch (e) {}
      }
    }

    function start() {
      cancelAnim();
      clearHold();
      ensureTrack();
      setX(0);
      measure();
      startPendulum(0);
    }

    // ---- Pointer / drag handling ----
    // Capture immediately so every move reaches us (prevents the page from
    // stealing the horizontal gesture after the first couple of px).
    bc.addEventListener('pointerdown', function (e) {
      pointerDown = true;
      dragging = false;
      activeId = e.pointerId;
      startX = e.clientX;
      clearHold();
      measure();
      pos = clamp(Math.round(currentTranslate()), -overflow, 0);
      startPos = pos;
      cancelAnim();
      setX(pos);
      captured = false;
      try { bc.setPointerCapture(activeId); captured = true; } catch (err) {}
    });

    bc.addEventListener('pointermove', function (e) {
      if (!pointerDown || e.pointerId !== activeId) return;
      var dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) < DRAG_THRESHOLD) return; // still maybe a tap
      dragging = true;
      setX(clamp(startPos + dx, -overflow, 0));
      if (e.cancelable) e.preventDefault(); // we own the horizontal gesture
    }, { passive: false });

    function endDrag(e) {
      if (!pointerDown || (e && activeId != null && e.pointerId !== activeId)) return;
      if (captured && activeId != null) { try { bc.releasePointerCapture(activeId); } catch (err) {} }
      captured = false;
      pointerDown = false;
      dragging = false;
      activeId = null;
      clearHold();
      // hold where it is, then resume the pendulum from the current spot.
      var delay = HOLD_AFTER_DRAG;
      holdTimer = setTimeout(function () {
        holdTimer = null;
        measure();
        startPendulum(pos);
      }, delay);
    }
    bc.addEventListener('pointerup', endDrag);
    bc.addEventListener('pointercancel', endDrag);
    bc.addEventListener('lostpointercapture', function (e) {
      // safety: if capture is lost mid-drag, finalise like a release
      if (pointerDown) endDrag(e);
    });

    start();

    // TASK-1941: only re-run start() when the viewport WIDTH actually changes.
    // Height-only resizes (sticky header collapsing on scroll, mobile URL bar
    // show/hide) must NOT restart the pendulum, otherwise the row glides itself
    // sideways right as the header folds.
    var lastW = window.innerWidth;
    var rt = null;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastW) return; // width unchanged -> ignore
      lastW = window.innerWidth;
      if (rt) clearTimeout(rt);
      rt = setTimeout(start, 200);
    }, { passive: true });
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () { setTimeout(start, 50); });
    }
  }

  function initAll() {
    document.querySelectorAll('.ils-bc').forEach(setup);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();
